import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  MenuItem,
  Select,
  OutlinedInput,
  Box,
  Snackbar,
  Alert,
  TablePagination,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  fetchRoles,
  createRole,
  updateRole,
  deleteRole,
  assignPermissionsToRole,
  fetchPermissions,
} from "../../api";
import { useAuth } from "../../App";
import { hasPermission } from "../../utils/permissions";
import Forbidden403 from "../../components/common/Forbidden403";

function RoleTable({ roles, onEdit, onDelete, loading }) {
  if (loading)
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Permissions</TableCell>
          <TableCell sx={{ width: 250 }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {roles.map((role) => (
          <TableRow key={role.id}>
            <TableCell>{role.name}</TableCell>
            <TableCell>
              {role.permissions &&
                role.permissions.map((perm) => (
                  <Chip
                    key={perm}
                    label={perm}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
            </TableCell>
            <TableCell>
              <Button
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
                onClick={() => onEdit(role)}
              >
                Edit
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => onDelete(role)}
              >
                Delete
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RoleModal({
  open,
  onClose,
  role,
  onSave,
  loading,
  permissions,
  permissionsLoading,
  permissionsError,
}) {
  const isEdit = !!role;
  const [form, setForm] = useState({ name: "", permission_ids: [] });
  const [errors, setErrors] = useState({});
  const [permissionSearch, setPermissionSearch] = useState("");
  useEffect(() => {
    if (role)
      setForm({ name: role.name, permission_ids: role.permissions || [] });
    else setForm({ name: "", permission_ids: [] });
    setErrors({});
  }, [role, open]);
  const validate = () => {
    const e = {};
    if (!form.name) e.name = "Role name is required";
    return e;
  };
  const handleSave = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length === 0) {
      onSave(form);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit Role" : "Create Role"}</DialogTitle>
      <DialogContent>
        <TextField
          label="Role Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          fullWidth
          margin="normal"
          error={!!errors.name}
          helperText={errors.name}
        />
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Permissions
        </Typography>
        {/* <TextField
          size="small"
          placeholder="Search permissions..."
          value={permissionSearch}
          onChange={(e) => setPermissionSearch(e.target.value)}
          sx={{ width: 350, mb: 2 }}
        /> */}
        {permissionsLoading ? (
          <Box sx={{ py: 2 }}>
            <Typography>Loading permissions...</Typography>
          </Box>
        ) : permissionsError ? (
          <Alert severity="error">{permissionsError}</Alert>
        ) : (
          <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2, mt: 1 }}>
            <TextField
              size="small"
              placeholder="Search permissions..."
              value={permissionSearch}
              onChange={(e) => setPermissionSearch(e.target.value)}
              sx={{ width: 350, mb: 2 }}
            />
            {(() => {
              // Group permissions by prefix (before first dot or colon)
              const groups = {};
              permissions.forEach((perm) => {
                const match = perm.match(/^([a-zA-Z0-9_]+)[.:]/);
                const group = match ? match[1] : "Other";
                if (!groups[group]) groups[group] = [];
                groups[group].push(perm);
              });
              // Filter by search
              const search = permissionSearch.trim().toLowerCase();
              return Object.entries(groups)
                .filter(([group, perms]) => {
                  if (!search) return true;
                  // Show group if group name matches or any permission matches
                  return (
                    group.toLowerCase().includes(search) ||
                    perms.some((perm) => perm.toLowerCase().includes(search))
                  );
                })
                .map(([group, perms]) => {
                  // Filter permissions in group
                  const filteredPerms = search
                    ? perms.filter(
                        (perm) =>
                          perm.toLowerCase().includes(search) ||
                          group.toLowerCase().includes(search)
                      )
                    : perms;
                  if (filteredPerms.length === 0) return null;
                  const allChecked = filteredPerms.every((perm) =>
                    form.permission_ids.includes(perm)
                  );
                  const someChecked = filteredPerms.some((perm) =>
                    form.permission_ids.includes(perm)
                  );
                  return (
                    <Box key={group} sx={{ mb: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={(el) => {
                            if (el)
                              el.indeterminate = !allChecked && someChecked;
                          }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm((f) => ({
                                ...f,
                                permission_ids: Array.from(
                                  new Set([
                                    ...f.permission_ids,
                                    ...filteredPerms,
                                  ])
                                ),
                              }));
                            } else {
                              setForm((f) => ({
                                ...f,
                                permission_ids: f.permission_ids.filter(
                                  (p) => !filteredPerms.includes(p)
                                ),
                              }));
                            }
                          }}
                          style={{ marginRight: 8 }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          {group.charAt(0).toUpperCase() + group.slice(1)}{" "}
                          Permissions
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          ml: 3,
                        }}
                      >
                        {filteredPerms.map((perm) => (
                          <FormControlLabel
                            key={perm}
                            control={
                              <Checkbox
                                checked={form.permission_ids.includes(perm)}
                                onChange={(e) => {
                                  setForm((f) => ({
                                    ...f,
                                    permission_ids: e.target.checked
                                      ? [...f.permission_ids, perm]
                                      : f.permission_ids.filter(
                                          (p) => p !== perm
                                        ),
                                  }));
                                }}
                                size="small"
                              />
                            }
                            label={perm}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                });
            })()}
          </Box>
        )}
        <Box mt={2} mb={1}>
          {form.permission_ids.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {form.permission_ids.map((perm) => (
                <Chip key={perm} label={perm} size="small" />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function RolesPage() {
  const { auth, fetchWithAuth } = useAuth();
  // Permission check: superuser or has 'roles:list' permission
  const hasListPermission = hasPermission(auth?.user, "role.list");
  if (!hasListPermission) {
    return <Forbidden403 />;
  }
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const loadPermissions = async () => {
    setPermissionsLoading(true);
    setPermissionsError(null);
    try {
      const perms = await fetchPermissions(fetchWithAuth);
      setPermissions(perms);
    } catch (e) {
      setPermissionsError(e.message || "Failed to fetch permissions");
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const rolesRes = await fetchRoles(fetchWithAuth, page, rowsPerPage);
      // If the API returns paginated data with items and total
      if (rolesRes.items) {
        setRoles(rolesRes.items);
        setTotal(rolesRes.total || rolesRes.items.length);
      } else {
        // Fallback for non-paginated response
        setRoles(rolesRes);
        setTotal(rolesRes.length);
      }
    } catch (e) {
      setError(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadPermissions();
    // eslint-disable-next-line
  }, [fetchWithAuth, search, page, rowsPerPage]);

  const handleEdit = (role) => {
    setSelectedRole(role);
    setModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedRole(null);
    setModalOpen(true);
  };
  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role ${role.name}? This cannot be undone.`))
      return;
    try {
      setLoading(true);
      await deleteRole(fetchWithAuth, role.id);
      setSuccess("Role deleted successfully");
      setSnackbarOpen(true);
      await loadData();
    } catch (e) {
      setError(e.message || "Failed to delete role");
    } finally {
      setLoading(false);
    }
  };
  const handleSave = async (form) => {
    try {
      setLoading(true);
      setError(null);
      if (selectedRole) {
        await updateRole(fetchWithAuth, selectedRole.id, { name: form.name });
        await assignPermissionsToRole(
          fetchWithAuth,
          selectedRole.id,
          form.permission_ids
        );
        setSuccess("Role updated successfully");
      } else {
        const newRole = await createRole(fetchWithAuth, { name: form.name });
        await assignPermissionsToRole(
          fetchWithAuth,
          newRole.id,
          form.permission_ids
        );
        setSuccess("Role created successfully");
      }
      setSnackbarOpen(true);
      setModalOpen(false);
      await loadData();
    } catch (e) {
      setError(e.message || "Failed to save role");
    } finally {
      setLoading(false);
    }
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRoles = Array.isArray(roles) 
    ? roles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  return (
    <Container maxWidth="lg">
      <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
        <Typography variant="h4" gutterBottom>
          Roles Management
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Button variant="contained" onClick={handleCreate} disabled={loading}>
            Create Role
          </Button>
          <TextField
            size="small"
            placeholder="Search roles..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ width: 250 }}
          />
        </Box>
        <RoleTable
          roles={paginatedRoles}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 2 }}
        />
        <RoleModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          role={selectedRole}
          onSave={handleSave}
          loading={loading}
          permissions={permissions}
          permissionsLoading={permissionsLoading}
          permissionsError={permissionsError}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          message={success}
        />
      </Paper>
    </Container>
  );
}
